import React, { useState } from 'react';
import { CloudinaryUploader } from '../components/CloudinaryUploader';
import { Helmet } from 'react-helmet-async';

interface UploadedMedia {
  type: 'image' | 'video';
  url: string;
  timestamp: number;
}

const CloudinaryDemo = () => {
  const [uploadedMedia, setUploadedMedia] = useState<UploadedMedia[]>([]);
  const [activeTab, setActiveTab] = useState<'image' | 'video'>('image');

  const handleUploadComplete = (url: string) => {
    setUploadedMedia(prev => [
      {
        type: activeTab,
        url,
        timestamp: Date.now()
      },
      ...prev
    ]);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Cloudinary Media Upload | Rion Wildlife</title>
        <meta name="description" content="Upload and manage media with Cloudinary" />
      </Helmet>

      <h1 className="text-3xl font-bold mb-6">Media Upload</h1>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="mb-4 border-b border-gray-200">
            <div className="flex">
              <button
                className={`py-2 px-4 ${activeTab === 'image' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
                onClick={() => setActiveTab('image')}
              >
                Image Upload
              </button>
              <button
                className={`py-2 px-4 ${activeTab === 'video' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
                onClick={() => setActiveTab('video')}
              >
                Video Upload
              </button>
            </div>
          </div>
          
          <CloudinaryUploader 
            mediaType={activeTab}
            onUploadComplete={handleUploadComplete}
            buttonText={`Upload ${activeTab === 'image' ? 'Image' : 'Video'}`}
          />
          
          <div className="mt-8">
            <h2 className="text-xl font-semibold mb-4">Upload Tips</h2>
            <ul className="list-disc pl-5 space-y-2 text-gray-700">
              <li>Maximum file size: 10MB for images, 100MB for videos</li>
              <li>Supported image formats: JPG, PNG, WebP, GIF</li>
              <li>Supported video formats: MP4, WebM, MOV</li>
              <li>For best results, use high-resolution images (at least 1200px wide)</li>
              <li>Videos will be automatically optimized for web delivery</li>
            </ul>
          </div>
        </div>
        
        <div>
          <h2 className="text-xl font-semibold mb-4">Recent Uploads</h2>
          {uploadedMedia.length === 0 ? (
            <div className="text-gray-500 italic">No media uploaded yet</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {uploadedMedia.map((media, index) => (
                <div key={index} className="border rounded-lg overflow-hidden">
                  <div className="aspect-video bg-gray-100 flex items-center justify-center">
                    {media.type === 'image' ? (
                      <img 
                        src={media.url} 
                        alt={`Upload ${index}`} 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <video 
                        src={media.url} 
                        controls 
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                  <div className="p-3">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium capitalize">{media.type}</span>
                      <span className="text-xs text-gray-500">
                        {new Date(media.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => navigator.clipboard.writeText(media.url)}
                        className="text-xs text-blue-500 hover:underline"
                      >
                        Copy URL
                      </button>
                      <a 
                        href={media.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-blue-500 hover:underline"
                      >
                        Open
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CloudinaryDemo; 